// Time calculation utilities for task positioning and scheduling
// CREATED: Centralized functions for time-based calculations used across timeline components
// UPDATED: Added conflict detection functions for temporal overlap checking

const MINUTES_PER_DAY = 24 * 60;

/**
 * Converts a time string (HH:MM) to minutes since midnight
 * @param time - Time string in HH:MM format
 * @returns Number of minutes since midnight
 */
export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Converts minutes since midnight to a time string (HH:MM)
 * Handles negative values and values > 1440 by wrapping around
 * @param minutes - Number of minutes since midnight
 * @returns Time string in HH:MM format
 */
export function minutesToTime(minutes: number): string {
  const normalizedMinutes = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(normalizedMinutes / 60);
  const mins = normalizedMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Converts a time string to a percentage of the day (0-100)
 * Used for positioning elements on timeline visualizations
 * @param time - Time string in HH:MM format
 * @returns Percentage of the day (0-100)
 */
export function timeToPercent(time: string): number {
  return (timeToMinutes(time) / MINUTES_PER_DAY) * 100;
}

/**
 * Converts a duration in minutes to a percentage of the day (0-100)
 * Used for sizing elements on timeline visualizations
 * @param durationMinutes - Duration in minutes
 * @returns Percentage of the day (0-100)
 */
export function durationToPercent(durationMinutes: number): number {
  return (durationMinutes / MINUTES_PER_DAY) * 100;
}

/**
 * Converts a time string to pixel offset for positioning
 * @param time - Time string in HH:MM format
 * @param pixelsPerMinute - Scaling factor (default: 1 pixel per minute)
 * @returns Pixel offset from midnight
 */
export function timeToPixelOffset(time: string, pixelsPerMinute: number = 1): number {
  return timeToMinutes(time) * pixelsPerMinute;
}

/**
 * Converts a duration in minutes to pixels
 * @param durationMinutes - Duration in minutes
 * @param pixelsPerMinute - Scaling factor (default: 1 pixel per minute)
 * @returns Width/height in pixels
 */
export function durationToPixels(durationMinutes: number, pixelsPerMinute: number = 1): number {
  return durationMinutes * pixelsPerMinute;
}

/**
 * Rounds minutes to the nearest interval
 * @param minutes - Minutes to round
 * @param interval - Rounding interval in minutes (default: 15)
 * @returns Rounded minutes
 */
export function roundToInterval(minutes: number, interval: number = 15): number {
  return Math.round(minutes / interval) * interval;
}

/**
 * Rounds minutes to the nearest quarter hour (15 minutes)
 * Convenience wrapper around roundToInterval
 * @param minutes - Minutes to round
 * @returns Rounded minutes
 */
export function roundToQuarterHour(minutes: number): number {
  return roundToInterval(minutes, 15);
}

/**
 * Represents a time slot with a start time and duration
 */
export interface TimeSlot {
  startTime: string;
  duration: number;
}

/**
 * Checks if two time slots overlap
 * Uses the standard interval overlap algorithm: start1 < end2 && start2 < end1
 * @param slot1 - First time slot
 * @param slot2 - Second time slot
 * @returns True if the slots overlap, false otherwise
 */
export function doTimeSlotsOverlap(slot1: TimeSlot, slot2: TimeSlot): boolean {
  const start1 = timeToMinutes(slot1.startTime);
  const end1 = start1 + slot1.duration;
  const start2 = timeToMinutes(slot2.startTime);
  const end2 = start2 + slot2.duration;
  return start1 < end2 && start2 < end1;
}

/**
 * Checks if a new time slot conflicts with any existing slots
 * @param newSlot - The new time slot to check
 * @param existingSlots - Array of existing time slots
 * @returns True if there is at least one conflict, false otherwise
 */
export function hasConflictWithExisting(
  newSlot: TimeSlot,
  existingSlots: TimeSlot[]
): boolean {
  return existingSlots.some(existing => doTimeSlotsOverlap(newSlot, existing));
}

/**
 * Finds all time slots that conflict with a new slot
 * @param newSlot - The new time slot to check
 * @param existingSlots - Array of existing time slots
 * @returns Array of conflicting time slots
 */
export function findAllConflicts(
  newSlot: TimeSlot,
  existingSlots: TimeSlot[]
): TimeSlot[] {
  return existingSlots.filter(existing => doTimeSlotsOverlap(newSlot, existing));
}
